import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiRequest } from '../../services/api';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import DataTable from '../../components/Shared/DataTable';
import { schoolAdminNavItems } from '../../constants/navigation';
import EmptyState from '../../components/Shared/EmptyState';

const StudentsPage = () => {
  const { currentUser } = useAuth();
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [teacherFilter, setTeacherFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Fetch teacher list for filter dropdown
  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        const data = await apiRequest('/api/school-admin/teachers?per_page=100', 'GET');
        setTeachers(data.teachers || []);
      } catch (err) {
        // Non-critical
      }
    };
    fetchTeachers();
  }, []);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/api/school-admin/students?page=${currentPage}&per_page=20`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (teacherFilter) url += `&teacher_id=${teacherFilter}`;
      if (gradeFilter) url += `&grade=${gradeFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;

      const data = await apiRequest(url, 'GET');
      setStudents(data.students || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, teacherFilter, gradeFilter, statusFilter]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const studentColumns = [
    {
      key: 'username',
      label: 'Username',
      sortable: true,
      render: (s) => <span className="font-medium text-gray-900">{s.username}</span>,
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (s) => (
        <span>{s.first_name || ''} {s.last_name || ''}</span>
      ),
    },
    {
      key: 'grade_level',
      label: 'Grade',
      sortable: true,
      render: (s) => <span className="capitalize">{s.grade_level || 'N/A'}</span>,
    },
    {
      key: 'teacher_name',
      label: 'Teacher',
      sortable: true,
      render: (s) => <span>{s.teacher_name || 'Unassigned'}</span>,
    },
    {
      key: 'sessions_count',
      label: 'Sessions',
      sortable: true,
      render: (s) => <span className="font-medium">{s.sessions_count || 0}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (s) => {
        if (!s.is_active) return <span className="text-gray-500">Inactive</span>;
        if (s.activation_code_used) return <span className="text-green-600 font-medium">Active</span>;
        return <span className="text-yellow-600">Pending</span>;
      },
    },
  ];

  if (loading && students.length === 0) {
    return (
      <DashboardLayout title="Students" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600">Loading students...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Students" navItems={schoolAdminNavItems} userRole="school-admin" userName={currentUser?.full_name || 'Admin'}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-gray-900">Student Management</h2>
        <span className="text-sm text-gray-500">{pagination?.total || 0} total students</span>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
          Error: {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="text"
          placeholder="Search students..."
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500"
        />
        <select
          value={teacherFilter}
          onChange={(e) => { setTeacherFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white text-sm"
        >
          <option value="">All Teachers</option>
          {teachers.filter(t => t.activation_code_used).map((t) => (
            <option key={t.id} value={t.id}>{t.full_name}</option>
          ))}
        </select>
        <select
          value={gradeFilter}
          onChange={(e) => { setGradeFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white text-sm"
        >
          <option value="">All Grades</option>
          <option value="elementary">Elementary</option>
          <option value="middle">Middle</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white text-sm"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {!loading && students.length === 0 && !searchQuery && !teacherFilter && !gradeFilter && !statusFilter ? (
        <EmptyState
          icon="&#128101;"
          title="No Students Yet"
          description="Teachers will create student codes once they are set up on the platform."
        />
      ) : (
        <DataTable
          columns={studentColumns}
          data={students}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          emptyMessage="No students found matching your filters."
        />
      )}
    </DashboardLayout>
  );
};

export default StudentsPage;
